import Barchart from 'components/molecules/Barchart';
import Legend from 'components/molecules/Legend';
import PointsChart from 'components/molecules/PointsChart';
import { commonClassName, commonHeaderClass } from 'components/pages/PromoScorecard';
import { colorConstants } from 'config/colorConstants';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    calculateContainerHeight,
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getConfig,
    parseAttributeType,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { CustomSelect } from '../BumpReport/MainSection';
import DownSvg from 'assets/svg/Down';
import { Button, Input, Switch } from 'antd';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { UserContext } from 'contexts/ConfigContext';
import _ from 'lodash';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { salesGraphWidth } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import TileButton from 'components/atoms/TileButton';
import { yAxisLabelClass } from '../GrowthAnalysis/ExpandedView';
import { FilterContextData } from 'contexts/FilterContext';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import MultiSelectionDropdownV2 from 'components/molecules/MultiSelectionDropdownV2';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { defaultApplyFilter } from 'components/pages/GrowthAnalysis';
import { useFetchDropdown2Data } from 'hooks/priceLadder/useFetchDropdown2Data';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { priceLadderChart1Download } from 'parser/graphToExcelDownloadParser';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PRICE_LADDER_CHART1_DROPDOWN_CHANGED,
    PRICE_LADDER_CHART1_LEGEND_CLICKED,
    PRICE_LADDER_CHART1_TAB_CHANGED,
} from 'utils/mixpanelEventNames';
import { ConfigKeys } from 'utils/Enums';

//price ladder label
const mainLabel = 'Price Ladder ';
const mraupLabel = 'MRAUP';
const salesPercentageLabel = 'Sales %';

export const chartHeight = calculateContainerHeight(3, 200);
export const yAxisWidth = calculateFontSize(window.innerWidth, 60);

export const priceLadderContainerId = 'price-ladder-chart-container';

interface Props {
    attributeTypesList: any[];
    setAttributeTypesList: Function;
    selectPPG: any;
    setSelectPPG: Function;
    subTileState: any;
    setSubTileState: Function;
    tileData: any;
    widthConfig: any;
    data: any;
    loading: boolean;
    error: any;
    getPriceLadderData: Function;
    setDropdownFilterIds: Function;
    offset: number;
    infiniteScrollWidthRef: any;
    setApplyFilterTempLoading: Function;
    setError: Function;
    applyFilterTempLoading: boolean;
    setSelectedAttributeItem: Function;
    selectedAttributeItem: string;
    count: number;
    thresholdValue: string;
    setThresholdValue: Function;
    setDummyThresholdRender: Function;
}

const BarPointChartContainer: FC<Props> = ({
    attributeTypesList,
    selectPPG,
    setAttributeTypesList,
    setSelectPPG,
    setSubTileState,
    subTileState,
    tileData,
    widthConfig,
    data,
    loading,
    error,
    infiniteScrollWidthRef,
    setDropdownFilterIds,
    getPriceLadderData,
    offset,
    setError,
    applyFilterTempLoading,
    setApplyFilterTempLoading,
    setSelectedAttributeItem,
    selectedAttributeItem,
    count,
    thresholdValue,
    setThresholdValue,
    setDummyThresholdRender,
}) => {
    //this variables contains the graph data and tooltip data
    const { appConfig } = useContext(UserContext);
    const priceLadderConfig = getConfig('priceLadder', appConfig);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const { decimalForPercentage, decimalForNonPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const {
        dropdown2Filter,
        dimensionConfig: { excludedDimensions, defaultDimension },
    } = priceLadderConfig;
    const [search, setSearch] = useState('');

    const currency = fetchCurrency(appConfig);

    const y1AxisLabel = `Price ${currency}`;
    const y2AxisLabel = `Sales ${currency}`;

    const [filteredLegends, setFilteredLegends] = useState<any[]>([]);
    const {
        data: applyFilterData,
        loading: applyFilterLoading,
        error: applyFilterError,
        applyFilterMutate,
    } = useApplyFilterMutation({ onComplete: onApplyFilterComplete, onError: onApplyFilterError });

    const {
        data: dropdownApplyFilterData,
        loading: dropdownApplyFilterDataLoading,
        error: dropdownApplyFilterDataError,
        applyFilterMutate: dropdownApplyFilterDataMutate,
    } = useApplyFilterMutation({ onComplete: onDropdownApplyFilterComplete, onError: onDropdownApplyFilterError });

    const {
        data: dropdownData,
        loading: dropdownLoading,
        error: dropdownError,
        count: dropdownCount,
        defaultCompetitorsList: defaultCompetitorsList,
        fetchDropdownData,
    } = useFetchDropdown2Data();

    const [dropdown2FilterData, setDropdown2FilterData] = useState<any[]>([]);

    const dropdownFilterRef = useRef<any>();
    const dropdownOffsetRef = useRef(0);
    const productName = useRef('');
    const graphRef = useRef<any>();

    const hideMraup = priceLadderConfig?.hideMraup || false;
    //width config
    const { containerWidth, graphViewContainerWidth, perGraphComponentWidth } = widthConfig;

    //filter context
    const {
        allDimAndAttributeTypeList,
        allCheckedAttribute,
        idForRestOfComponent,
        timePeriodSelection,
        timeSelectionLabel,
    } = useContext(FilterContextData);
    //point chart colors
    const pointColors = priceLadderConfig.legendMapping?.[subTileState?.key]?.map((item: any) => item.color);

    //getting the dim2 and dim1 attribute types depending on the tab
    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            let data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                if (priceLadderConfig.tileData?.length > 1) {
                    data = data?.filter(
                        (item: any) =>
                            item.dimension === (tileData.key === priceLadderConfig.tileData[0].key ? 'dim2' : 'dim1'),
                    );
                    setSelectPPG(data[tileData.key === priceLadderConfig.tileData[0].key ? 2 : 10]);
                } else {
                    setSelectPPG(
                        data[
                            tileData.key === priceLadderConfig.tileData[0].key
                                ? Math.max(
                                      0,
                                      data?.findIndex(
                                          (item: any) => item?.label?.toLowerCase() === defaultDimension?.toLowerCase(),
                                      ),
                                  )
                                : 10
                        ],
                    );
                }
                setAttributeTypesList(data);
            }
        }
    }, [allDimAndAttributeTypeList?.loading, tileData]);

    useEffect(() => {
        let applyFilterTemp: any[] = [];
        if (tileData.key !== priceLadderConfig.tileData[0].key) {
            dropdown2Filter?.forEach((attrType: any) => {
                if (attrType in allCheckedAttribute && Object.keys(allCheckedAttribute[attrType])?.length > 0) {
                    Object.keys(allCheckedAttribute[attrType])?.map((item) => {
                        applyFilterTemp.push(allCheckedAttribute[attrType][item]);
                    });
                }
            });
        }
        setDropdown2FilterData(applyFilterTemp);
    }, [idForRestOfComponent, tileData]);

    // const dropdown2FilterData = useMemo(() => {
    //     let applyFilterTemp: any[] = [];
    //     if (tileData.key !== priceLadderConfig.tileData[0].key) {
    //         dropdown2Filter?.forEach((attrType: any) => {
    //             if (attrType in allCheckedAttribute && Object.keys(allCheckedAttribute[attrType])?.length > 0) {
    //                 Object.keys(allCheckedAttribute[attrType])?.map((item) => {
    //                     applyFilterTemp.push(allCheckedAttribute[attrType][item]);
    //                 });
    //             }
    //         });
    //     }
    //     return applyFilterTemp;
    // }, [idForRestOfComponent, tileData]);

    useEffect(() => {
        if (dropdown2FilterData?.length === 0) {
            if (tileData.key !== priceLadderConfig.tileData[0].key) {
                onDropdownApplyFilterComplete(idForRestOfComponent);
            } else setDropdownFilterIds({});
            return;
        }
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
        tempApplyFilterVariable.jsonstring_dim1.inclusion = [];
        tempApplyFilterVariable.jsonstring_dim2.inclusion = [];
        dropdown2FilterData?.map((item) => {
            if (item?.dimension === 'dim1') {
                tempApplyFilterVariable.jsonstring_dim1.inclusion.push({
                    ...item,
                });
            } else {
                tempApplyFilterVariable.jsonstring_dim2.inclusion.push({
                    ...item,
                });
            }
        });
        if (!tempApplyFilterVariable.jsonstring_dim1.inclusion?.length)
            delete tempApplyFilterVariable.jsonstring_dim1.inclusion;
        if (!tempApplyFilterVariable.jsonstring_dim2.inclusion?.length)
            delete tempApplyFilterVariable.jsonstring_dim2.inclusion;
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
        setApplyFilterTempLoading(true);
        dropdownApplyFilterDataMutate({ variables: tempApplyFilterVariable });
    }, [dropdown2FilterData]);

    //filter the data when user disables/enables the legends
    const customData = useMemo(() => {
        const currentData = _.cloneDeep(data?.data?.[subTileState.key]) || [];
        let tempLineData = [...(data?.lineData?.[subTileState.key] || [])];
        const isSales = filteredLegends.find((item) => item.label === y2AxisLabel);
        const isMraup = filteredLegends.find((item) => item.label === mraupLabel) || hideMraup;
        const currency = fetchCurrency(appConfig) || '';

        //data filter logic
        //filter the data according to the legends selected
        if (filteredLegends?.length > 0) {
            const filteredLabelArray = filteredLegends.map((item) => item.label);
            //bar point data
            currentData.forEach((item: any) => {
                if (item && Array.isArray(item)) {
                    item.forEach((data) => {
                        if (isSales) data.sales = null;
                        if (isMraup) data.mraup = null;
                        if (filteredLabelArray.includes(data?.category)) {
                            data.price = null;
                        }
                    });
                }
            });
            if (isMraup) {
                tempLineData = tempLineData.map((item, idx) => {
                    return {
                        ...item,
                        mraup: -1000000, //giving a negative value here so that tooltip appears, giving a null will make the tooltip disappear
                    };
                });
            }
        }
        const categoryLabelColorMapping: any = {};
        priceLadderConfig.legendMapping?.[subTileState.key]?.forEach((item: any) => {
            categoryLabelColorMapping[item?.label] = item?.color;
        });
        //chart tooltip items
        const tooltipData = currentData.map((item: any) => {
            let defaultTooltipItem: any[] = [];
            //adding sales percentage in tooltip
            defaultTooltipItem.push({
                name: salesPercentageLabel,
                value: formatNumWithKMBAlt(item?.[0]?.salesPercentage, decimalForPercentage) + '%',
                title: item?.[0]?.retailer,
            });
            const tooltipForEachItem: any[] = Array.isArray(item)
                ? item
                      .filter((el, index: number) => {
                          if (isSales) return el.price !== null;
                          if (el.category === y2AxisLabel) return true;
                          return el.price !== null;
                      })
                      .map((tooltipItem, idx: number) => {
                          return {
                              name: tooltipItem?.category,
                              value:
                                  currency +
                                  formatNumWithKMBAlt(
                                      tooltipItem?.category === y2AxisLabel ? tooltipItem.sales : tooltipItem.price,
                                      decimalForNonPercentage,
                                      decimalForNonPercentage,
                                  ),
                              title: tooltipItem.retailer,
                              color: categoryLabelColorMapping[tooltipItem?.category],
                          };
                      })
                : [];

            return [...tooltipForEachItem, ...defaultTooltipItem];
        });
        if (!isMraup) {
            tooltipData.forEach((item: any, index: number) => {
                item.push({
                    name: mraupLabel,
                    value: formatNumWithKMBAlt(
                        tempLineData?.[index]?.mraup,
                        decimalForNonPercentage,
                        decimalForNonPercentage,
                    ),
                    title: tempLineData?.[index]?.retailer,
                    color: categoryLabelColorMapping[mraupLabel],
                });
            });
        }
        return {
            BarPointData: currentData,
            lineData: tempLineData,
            toolTipData: tooltipData,
        };
    }, [filteredLegends, subTileState, data]);

    const combinedLoading =
        allDimAndAttributeTypeList?.loading ||
        loading ||
        applyFilterLoading ||
        (dropdownLoading && !search) ||
        applyFilterTempLoading ||
        dropdownApplyFilterDataLoading;
    const isFirstItemSticky = tileData.key === priceLadderConfig.tileData[1]?.key || false;

    productName.current = data?.product || productName.current || '';

    const formattedMainLabel =
        tileData.key === priceLadderConfig.tileData[0].key && priceLadderConfig.tileData?.length === 1
            ? `${mainLabel} by ${selectPPG?.label}`
            : tileData.key === priceLadderConfig.tileData[0].key
            ? `${mainLabel} of ${productName.current} by ${selectPPG?.label}`
            : `${mainLabel} of ${productName.current} by Competitor ${selectPPG?.label}`;

    //infinite scroller function
    function infiniteScrollOnNext(width: number) {
        if (loading) return;
        infiniteScrollWidthRef.current = width - 200;
        if (tileData.key === priceLadderConfig.tileData[0].key) {
            getPriceLadderData([1, 2], offset + priceLadderConfig?.limit, true);
        }
    }

    //call apply filter
    function prepareApplyFilterVariableAndExecute(checkedOptions: any = {}) {
        const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilter);
        tempApplyFilterVariable.jsonstring_dim1.inclusion = [];
        tempApplyFilterVariable.jsonstring_dim2.inclusion = [];
        Object.keys(checkedOptions)?.map((item) => {
            const attributeItem = checkedOptions[item];
            if (attributeItem?.dimension === 'dim1') {
                tempApplyFilterVariable.jsonstring_dim1.inclusion.push({
                    ...attributeItem,
                });
            } else {
                tempApplyFilterVariable.jsonstring_dim2.inclusion.push({
                    ...attributeItem,
                });
            }
        });
        if (!tempApplyFilterVariable.jsonstring_dim1.inclusion?.length)
            delete tempApplyFilterVariable.jsonstring_dim1.inclusion;
        if (!tempApplyFilterVariable.jsonstring_dim2.inclusion?.length)
            delete tempApplyFilterVariable.jsonstring_dim2.inclusion;
        tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
        tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
        setApplyFilterTempLoading(true);
        applyFilterMutate({ variables: tempApplyFilterVariable });
    }

    function prepareVariablesAndFetchDropdownData(loading: boolean, offset: number, search: string = '') {
        if (loading) return;
        fetchDropdownData({
            variables: {
                defaultCompetitors: JSON.stringify(offset === 0 ? [] : defaultCompetitorsList),
                dimension: selectPPG?.dimension,
                attributeType: _.snakeCase(selectPPG?.label),
                limit: globalFilterConfig?.limit,
                globalFilters: JSON.stringify(idForRestOfComponent),
                offset: offset,
                timePeriod: timePeriodSelection?.toLowerCase(),
                globalFiltersSubset: JSON.stringify(dropdownFilterRef.current),
                searchKeyword: search,
            },
        });
        if (!search) setApplyFilterTempLoading(true);
    }

    function onDropdownApplyFilterComplete(payload: any) {
        dropdownFilterRef.current = payload;
        prepareVariablesAndFetchDropdownData(dropdownLoading, dropdownOffsetRef.current);
    }

    useEffect(() => {
        if (dropdownFilterRef.current && tileData.key === priceLadderConfig.tileData?.[1]?.key) {
            dropdownOffsetRef.current = 0;
            setSearch('');
            prepareVariablesAndFetchDropdownData(dropdownLoading, dropdownOffsetRef.current);
        }
    }, [selectPPG]);

    useEffect(() => {
        if (dropdownError && !search) {
            setError(dropdownError);
            setApplyFilterTempLoading(false);
        }
    }, [dropdownError]);

    //apply filter error
    function onDropdownApplyFilterError(error: any) {
        setError(error);
        setApplyFilterTempLoading(false);
    }

    //apply filter on complete handler
    function onApplyFilterComplete(payload: any) {
        // console.log(payload, 'logging pay');
        setDropdownFilterIds(payload);
    }

    //apply filter error
    function onApplyFilterError(error: any) {
        setApplyFilterTempLoading(false);
    }

    function onCheckboxChange(data: any) {
        prepareApplyFilterVariableAndExecute(data);
    }

    const commonError = error || (dropdownError && !search);
    const minVal = data?.[`min${_.capitalize(subTileState?.key)}`];
    const maxVal = data?.[`max${_.capitalize(subTileState?.key)}`];

    const excelData = useMemo(() => {
        return priceLadderChart1Download(customData?.BarPointData, filteredLegends, selectPPG?.label);
    }, [filteredLegends, subTileState, data]);

    const handleBarItemClick = (item: string = '') => {
        setSelectedAttributeItem(item);
    };
    return (
        <div ref={graphRef} className={commonClassName}>
            <div className="flex justify-between mb-4">
                <div className="flex items-center gap-4">
                    {productName.current && (
                        <p className="font-lg text-headerColor font-medium">{formattedMainLabel}</p>
                    )}
                    <div className="flex justify-between items-center">
                        <h5 className="font-medium font-xs m-0 !text-fontGray mr-3">Attributes</h5>
                        <CustomSelect
                            className="dropdown-height dropdown-width-alt uniqSelPricePointAttributeDropDown"
                            suffixIcon={<DownSvg />}
                            value={selectPPG?.value}
                            disabled={combinedLoading}
                            options={attributeTypesList}
                            onChange={(val: any, item: any) => {
                                setSelectPPG(item);
                                setSelectedAttributeItem('');
                                //mixpanel event
                                trackEventMixpanel(PRICE_LADDER_CHART1_DROPDOWN_CHANGED, {
                                    attributeType: item?.label,
                                });
                            }}
                        />
                    </div>
                    {tileData.key !== priceLadderConfig.tileData[0].key && (
                        <MultiSelectionDropdownV2
                            disabled={applyFilterLoading}
                            search={search}
                            setSearch={setSearch}
                            setApplyFilterTempLoading={setApplyFilterTempLoading}
                            fetchDropdownData={prepareVariablesAndFetchDropdownData}
                            offset={dropdownOffsetRef}
                            options={dropdownData}
                            loading={dropdownLoading}
                            count={dropdownCount}
                            onCheckboxChange={onCheckboxChange}
                            attributeType={selectPPG}
                            dropdownWindowHeight={228}
                        />
                    )}
                </div>
                <div className="flex justify-center gap-4 items-end">
                    {priceLadderConfig?.isThresholdDropdownEnabled ? (
                        <div>
                            <p className="text-greyFont font-medium pb-1 font-xs">Sales Threshold ($ or EQ Sales)</p>
                            <div className="flex items-center gap-1">
                                <Input
                                    type={'number'}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !combinedLoading) {
                                            setDummyThresholdRender((prev: number) => prev + 1);
                                        }
                                    }}
                                    value={thresholdValue}
                                    onChange={(e) => {
                                        setThresholdValue(e.target.value);
                                    }}
                                    placeholder="Enter threshold"
                                    style={{ width: 150, borderRadius: 6 }}
                                />
                                <Button
                                    disabled={combinedLoading}
                                    type="primary"
                                    style={{ borderRadius: 6, fontSize: 13 }}
                                    onClick={() => {
                                        setDummyThresholdRender((prev: number) => prev + 1);
                                    }}
                                >
                                    Submit
                                </Button>
                            </div>
                        </div>
                    ) : null}
                    <TileButton
                        onClick={(item: any) => {
                            setSubTileState(item);
                            //mixpanel event
                            trackEventMixpanel(PRICE_LADDER_CHART1_TAB_CHANGED, {
                                tabName: item?.label,
                            });
                        }}
                        activeKey={subTileState.key}
                        data={priceLadderConfig.subTileData}
                    />
                    {!combinedLoading && (
                        <div className="flex items-center gap-2">
                            <CopyToClipBoard refOfComponent={graphRef} />

                            <DownloadExcel
                                title={'priceLadderChart'}
                                excelData={excelData.data}
                                headers={excelData.headers}
                            />
                        </div>
                    )}
                </div>
            </div>
            {combinedLoading ? (
                <div style={{ height: chartHeight + 50 }}>
                    <Loader />
                </div>
            ) : commonError ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: chartHeight + 50,
                    }}
                >
                    <ErrorBox msg={String(commonError)} />
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center my-4">
                        <p className={`${yAxisLabelClass} w-[120px`}>{y1AxisLabel}</p>
                        <Legend
                            data={priceLadderConfig?.legendMapping[subTileState.key]}
                            onClick={(item: any) => {
                                const itemIndex = filteredLegends.findIndex((legend) => legend?.label === item?.label);
                                //if item already present, then remove it
                                if (itemIndex > -1) {
                                    const tempLegends = [...filteredLegends];
                                    tempLegends.splice(itemIndex, 1);

                                    //mixpanel event
                                    trackEventMixpanel(PRICE_LADDER_CHART1_LEGEND_CLICKED, {
                                        legendName: item?.label,
                                        isSelected: false,
                                        tabName: subTileState?.label,
                                    });
                                    return setFilteredLegends(tempLegends);
                                }
                                //else set the new item
                                setFilteredLegends([...filteredLegends, item]);
                                //mixpanel event
                                trackEventMixpanel(PRICE_LADDER_CHART1_LEGEND_CLICKED, {
                                    legendName: item?.label,
                                    isSelected: true,
                                    tabName: subTileState?.label,
                                });
                            }}
                            selected={filteredLegends}
                        />
                        <p className={`w-[130px] whitespace-nowrap ${yAxisLabelClass} flex-[0] text-right mr-2`}>
                            {y2AxisLabel}
                        </p>
                    </div>
                    <div className="flex">
                        {/* Y1 AXIS */}
                        <Barchart
                            // padding={[0, 0, 0, 48]}
                            padding={[0, 0, -2, 0]}
                            barWidth={30}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`price-ladder-bar-point-y1-${tileData.key}`}
                            fields={['date', 'value']}
                            //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                            maxBound={maxVal}
                            minBound={scaleMinValue(30, minVal)}
                            // minBound={minVal}
                            width={yAxisWidth}
                            colorKey={'type'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, 2, 2);
                            }}
                            // Yline
                            YGrid={false}
                        />
                        <HorizontalInfiniteScroller
                            id={priceLadderContainerId}
                            hasMore={
                                tileData.key === priceLadderConfig.tileData[0].key &&
                                customData.BarPointData?.length < count
                            }
                            scrollThreshold={0.9}
                            toScrollWidth={infiniteScrollWidthRef.current}
                            additionalDependency={[selectedAttributeItem]}
                            onNext={infiniteScrollOnNext}
                        >
                            <div id={priceLadderContainerId} className="relative overflow-x-auto w-full pb-[40px]">
                                <div className="flex">
                                    {customData?.BarPointData?.map((item: any, index: number) => {
                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    position: index === 0 && isFirstItemSticky ? 'sticky' : 'static',
                                                    left: index === 0 && isFirstItemSticky ? 0 : 'auto',
                                                    background:
                                                        index === 0 && isFirstItemSticky
                                                            ? colorConstants.grey
                                                            : selectedAttributeItem === item?.[0]?.retailer
                                                            ? colorConstants.grey
                                                            : 'transparent',
                                                    zIndex: index === 0 && isFirstItemSticky ? 10 : 'auto',
                                                    borderRight:
                                                        index === 0 && isFirstItemSticky
                                                            ? `1px dashed ${colorConstants.greyAlt3}`
                                                            : '',
                                                }}
                                            >
                                                <PointsChart
                                                    yAxisConfig={false}
                                                    showLegend={false}
                                                    chartContainer={`price-ladder-bar-point-main-${index}-${tileData.key}`}
                                                    height={chartHeight}
                                                    loading={false}
                                                    fields={['retailer', 'price', 'sales', 'category']}
                                                    dataset={item}
                                                    width={perGraphComponentWidth}
                                                    //includeBarChart={true}
                                                    pointColors={pointColors}
                                                    shapeTypes={['square']}
                                                    error={false}
                                                    maxBound={maxVal}
                                                    //minBound={minVal}
                                                    minBound={scaleMinValue(30, minVal)}
                                                    maxBarChart={data?.maxSales}
                                                    minBarChart={data?.minSales}
                                                    barColor={
                                                        index === 0 && isFirstItemSticky
                                                            ? colorConstants.violetAlt
                                                            : colorConstants.greyAlt2
                                                    }
                                                    pointSize={5}
                                                    disableIntervalLabel
                                                    includeBarChart
                                                    intervalLabelTooltipKey={'sales'}
                                                />
                                                <div
                                                    style={{
                                                        width: perGraphComponentWidth,
                                                        overflowWrap: 'anywhere',
                                                        // wordBreak: 'break-word',
                                                        fontSize: graphXAxisLabelFontSize,
                                                    }}
                                                    className="text-center p-[2px]"
                                                >
                                                    {item?.[0]?.retailer}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <PointsChart
                                    onItemClick={
                                        priceLadderConfig.tileData?.length !== 1 ? undefined : handleBarItemClick
                                    }
                                    additionalStyle={{ position: 'absolute', top: 0, left: 0, zIndex: 11 }}
                                    includePointsChart={false}
                                    includeLineChart
                                    yAxisConfig={false}
                                    showLegend={false}
                                    chartContainer={`price-ladder-bar-point-line-chart-${tileData.key}`}
                                    height={chartHeight}
                                    loading={false}
                                    fields={['retailer', 'price', 'sales', 'category', 'mraup']}
                                    dataset={customData.lineData}
                                    width={graphViewContainerWidth}
                                    //includeBarChart={true}
                                    pointColors={pointColors}
                                    shapeTypes={['square']}
                                    error={false}
                                    maxBound={maxVal}
                                    minBound={minVal}
                                    maxBarChart={data?.maxSales}
                                    minBarChart={data?.minSales}
                                    barColor={colorConstants.greyAlt2}
                                    pointSize={5}
                                    customTooltip={customData?.toolTipData}
                                    disableIntervalLabel
                                    lineChartColor={pointColors[pointColors?.length - 1]}
                                />
                            </div>
                        </HorizontalInfiniteScroller>
                        {/* Y2 AXIS */}
                        <Barchart
                            // padding={[0, 0, 0, 48]}
                            padding={[0, 0, 0, 10]}
                            barWidth={30}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`price-ladder-bar-point-y2-${tileData.key}`}
                            fields={['date', 'value']}
                            //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                            maxBound={data?.maxSales}
                            minBound={data?.minSales}
                            width={yAxisWidth + 10}
                            colorKey={'type'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, 2, 2);
                            }}
                            // Yline
                            YGrid={false}
                        />
                    </div>
                    <p className={`text-center !top-[-35px] mb-[-40px] relative ${yAxisLabelClass}`}>
                        {selectPPG?.label}
                    </p>
                </>
            )}
        </div>
    );
};

export default BarPointChartContainer;
